import axios from 'axios'
import store from '@/store'
import {config as settings} from '@/lib/config'
import {shouldPropagate, startSpan} from '@/lib/telemetry'

const instance = axios.create({
  baseURL: settings.baseURL,
})

/**
 * Get the authToken if needed
 * 
 * @param {Boolean} needToken 
 * @returns headers: { 'Authorization': `Bearer ${authToken}` } if true
 * @returns {} if false
 */

const getHeadersAuth = (needToken) => {
  if (needToken) {
    const authToken = store.state.user.authToken
    return {
      headers: { 'Authorization': `Bearer ${authToken}` }
    }
  }
  return {}
}

/**
 * Get the config to hit url with header
 * 
 * @param {Object} config 
 * {
      url: ApiUrl,
      method: httpMethod,
      data : data
    }
 * @param {Object} options 
    {needToken: true}
 * @returns {Object} config, {Object} headers
 */
const setConfig = (config, options) => {
  const headersAuth = getHeadersAuth(options.needToken)
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(headersAuth.headers || {})
    }
  }
}

const isFormData = (data) =>
  typeof FormData !== 'undefined' &&
  Object.prototype.toString.call(data) === '[object FormData]'


/**
 * Hit a request using instance function that uses axios
 * 
 * @param {Object} config 
 * {
      url: ApiUrl,
      method: httpMethod,
      data : data
    }
 * @param {Object} options 
    {needToken: true}
 * @returns {Response} // {Status, Response}
 */
export const doRequest = async (config, options={}) => {
  const reqConfig = setConfig(config, options)
  // Let the browser set Content-Type (including the multipart boundary) for
  // FormData bodies. A JSON default/override here breaks the upload endpoint.
  if (isFormData(reqConfig.data)) {
    delete reqConfig.headers['Content-Type']
    delete reqConfig.headers['content-type']
  }
  const method = (reqConfig.method || 'GET').toUpperCase()
  const span = startSpan(`${method} ${reqConfig.url || ''}`, {
    'http.request.method': method,
    'url.full': `${settings.baseURL || ''}${reqConfig.url || ''}`,
  }, 3)

  if (shouldPropagate()) {
    reqConfig.headers = { ...(reqConfig.headers || {}), traceparent: span.traceparent }
  }

  const startedAt = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()
  let response = null
  let failure = null
  try {
    response = await instance(reqConfig)
  } catch (error) {
    failure = error
    // this logic to handle expired token
    if(error.response){
      response = error.response
      if(response.status == 401)store.dispatch('user/logout')
    } else if (error.request) {
      response = {
        status: 503,
        data: errorMessage[503]
      }
    } else {
      response = {
        status: 500,
        data: error.message || errorMessage[500]
      }
    }
  }

  const finishedAt = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()
  const spanError = failure && (!failure.response || failure.response.status >= 500) ? failure : null
  span.end({
    'http.response.status_code': response ? response.status : 0,
    'http.client.request.duration_ms': Math.round(finishedAt - startedAt),
  }, spanError)

  return response
}

export const errorMessage = {
  400: `Bad Request`,
  401: `You're Not Authorized`,
  403: `This action is forbidden`,
  404: `Not Found`,
  500: `Internal Server Error`,
  503: `No Internet Connection`
}


export default {
  doRequest,
  errorMessage
} 
